import React, { useMemo, useState } from "react";
import {
  ArrowUpDown,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
} from "lucide-react";

export default function TailwindDataTable({ columns = [], data = [] }) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);

  // 🔍 Filter
  const filteredData = useMemo(() => {
    return data.filter((row) =>
      Object.values(row)
        .join(" ")
        .toLowerCase()
        .includes(globalFilter.toLowerCase())
    );
  }, [data, globalFilter]);

  // ↕ Sort
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  // 📄 Pagination
  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, pageIndex, pageSize]);

  // ✅ Row selection
  const toggleSelectAll = () => {
    if (selectedRows.length === paginatedData.length) setSelectedRows([]);
    else setSelectedRows(paginatedData.map((r) => r.id));
  };
  const toggleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const handleDeleteSelected = () => {
    alert(`Deleted ${selectedRows.length} items (simulate backend)`);
    setSelectedRows([]);
  };

  return (
    <div className="flex flex-col w-full rounded-xl bg-dry text-text border border-border shadow-sm overflow-hidden">
      {/* 🔍 Top Toolbar */}
      <div className="flex items-center justify-between px-4 py-6 border-b border-border bg-dry/90">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-text/70" />
          <input
            type="text"
            placeholder="Search..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="px-2 py-1.5 text-sm border border-border rounded-md bg-transparent text-text placeholder:text-text/60 focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none"
          />
        </div>

        <div className="flex items-center gap-3">
          {selectedRows.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-red-500 hover:bg-red-600 rounded-md shadow-sm"
            >
              <Trash2 className="w-4 h-4" />
              Delete ({selectedRows.length})
            </button>
          )}
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPageIndex(0);
            }}
            className="px-2 py-1 text-sm border border-border rounded-md bg-dry text-text focus:ring focus:ring-red-200"
          >
            {[5, 10, 20, 50].map((size) => (
              <option key={size} value={size} className="bg-dry text-text">
                Show {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 📊 Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-border border-b border-border text-text/80">
            <tr>
              <th className="px-4 py-2 text-center">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === paginatedData.length &&
                    paginatedData.length > 0
                  }
                  onChange={toggleSelectAll}
                  className="accent-red-500 w-4 h-4"
                />
              </th>
              {columns.map((col) => (
                <th
                  key={col.accessorKey}
                  className="px-4 py-2 font-semibold text-white bg-border uppercase tracking-wide text-xs cursor-pointer select-none"
                  onClick={() => handleSort(col.accessorKey)}
                >
                  <div className="flex items-center justify-between">
                    {col.header}
                    <ArrowUpDown
                      className={`w-3.5 h-3.5 transition ${
                        sortConfig.key === col.accessorKey
                          ? "text-red-500 rotate-180"
                          : "text-text/50"
                      }`}
                    />
                  </div>
                </th>
              ))}
              <th className="px-4 py-2 text-white text-xs uppercase">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, index) => (
                <tr
                  key={row.id}
                  className={`border-b border-border transition ${
                    selectedRows.includes(row.id)
                      ? "bg-red-100/20"
                      : index % 2 === 1
                      ? "bg-dry/80"
                      : "bg-dry"
                  } hover:bg-red-50/10`}
                >
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row.id)}
                      onChange={() => toggleSelectRow(row.id)}
                      className="accent-red-500 w-4 h-4"
                    />
                  </td>

                  {columns.map((col) => (
                    <td key={col.accessorKey} className="px-4 py-3 text-text">
                      {col.Cell
                        ? col.Cell({ value: row[col.accessorKey], row })
                        : row[col.accessorKey]}
                    </td>
                  ))}

                  <td className="px-4 py-3 text-right">
                    <button className="p-1.5 rounded-md hover:bg-red-50/10">
                      <MoreVertical className="w-4 h-4 text-text/60" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length + 2}
                  className="text-center py-6 text-text/50"
                >
                  No results found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 📄 Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-dry/90 text-xs text-text/80">
        <div>
          Showing {pageIndex * pageSize + 1}–
          {Math.min((pageIndex + 1) * pageSize, sortedData.length)} of{" "}
          {sortedData.length}
        </div>

        <div className="flex items-center gap-1">
          <button
            disabled={pageIndex === 0}
            onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
            className="p-1 rounded hover:bg-red-50/10 disabled:opacity-40 border border-border"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="p-1 text-text">
            Page {pageIndex + 1} / {Math.ceil(sortedData.length / pageSize)}
          </span>
          <button
            disabled={(pageIndex + 1) * pageSize >= sortedData.length}
            onClick={() =>
              setPageIndex((i) =>
                Math.min(i + 1, Math.ceil(sortedData.length / pageSize) - 1)
              )
            }
            className="p-1 rounded hover:bg-red-50/10 disabled:opacity-40 border border-border"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
